import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
  Rocket, Trophy, Target, Brain, Lightning, Clock, CheckCircle,
  Heart, Star, Fire, CaretRight, CaretLeft, X,
  BookOpen, GameController, Users, Question, House, Books,
  Headphones, Wind, Medal, Confetti, ArrowRight
} from '@phosphor-icons/react';
import SEO from '../components/SEO';

// Progress storage
const STORAGE_KEY = '9ready_onboarding';

const saveProgress = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const loadProgress = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? JSON.parse(saved) : null;
};

// Step Components
const steps = [
  'welcome',
  'why-revision',
  'how-revision-works',
  'beat-procrastination',
  'smart-targets',
  'planning',
  'study-environment',
  'focus-wellbeing',
  'final-challenge',
  'complete'
];

export default function GetReady() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState({
    motivation: null,
    distraction: null,
    target: { subject: '', topic: '', time: 30 },
    studyPlace: null,
    xp: 0,
    badges: []
  });
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved progress on mount
  useEffect(() => {
    const saved = loadProgress();
    if (saved && saved.currentStep !== undefined) {
      setCurrentStep(saved.currentStep);
      if (saved.userData) {
        setUserData(prev => ({ ...prev, ...saved.userData }));
      }
    }
    setIsLoaded(true);
  }, []);

  // Save progress on change (only after initial load)
  useEffect(() => {
    if (isLoaded) {
      saveProgress({ currentStep, userData });
    }
  }, [currentStep, userData, isLoaded]);

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    setUserData(prev => ({ ...prev, xp: prev.xp + 10 }));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const skipOnboarding = () => {
    navigate('/subjects');
  };

  const resetProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStep(0);
    setUserData({
      motivation: null,
      distraction: null,
      target: { subject: '', topic: '', time: 30 },
      studyPlace: null,
      xp: 0,
      badges: []
    });
  };

  const progress = ((currentStep) / (steps.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Get Ready — Build Your GCSE Study Plan"
        description="Set your motivation, beat procrastination and pick your first GCSE topic. Personalised onboarding for Year 7-11 students at Grade9Ready."
        path="/get-ready"
      />
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b-2 border-foreground">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <img src="/grade9ready-logo.png" alt="Grade9Ready" className="h-8 w-auto" />
              <span className="font-heading font-bold">Get Ready</span>
            </div>
            <div className="flex items-center gap-3">
              {/* XP Display */}
              <div className="flex items-center gap-1 px-3 py-1 bg-accent rounded-full border-2 border-foreground text-sm font-bold">
                <Star size={14} weight="fill" />
                <span>{userData.xp} XP</span>
              </div>
              {/* Skip Button */}
              <button
                onClick={skipOnboarding}
                className="text-sm text-muted-foreground hover:text-foreground"
                data-testid="skip-onboarding"
              >
                Skip
              </button>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="neo-progress !h-3">
            <motion.div
              className="neo-progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 text-center">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <WelcomeStep key="welcome" onNext={nextStep} playClick={() => {}} />
          )}
          {currentStep === 1 && (
            <WhyRevisionStep 
              key="why-revision" 
              onNext={nextStep} 
              onBack={prevStep}
              userData={userData}
              setUserData={setUserData}
              playClick={() => {}}
            />
          )}
          {currentStep === 2 && (
            <HowRevisionWorksStep 
              key="how-revision" 
              onNext={nextStep} 
              onBack={prevStep}
              playClick={() => {}}
            />
          )}
          {currentStep === 3 && (
            <BeatProcrastinationStep 
              key="procrastination" 
              onNext={nextStep} 
              onBack={prevStep}
              userData={userData}
              setUserData={setUserData}
              playClick={() => {}}
            />
          )}
          {currentStep === 4 && (
            <SmartTargetsStep 
              key="targets" 
              onNext={nextStep} 
              onBack={prevStep}
              userData={userData}
              setUserData={setUserData}
              playClick={() => {}}
            />
          )}
          {currentStep === 5 && (
            <PlanningStep 
              key="planning" 
              onNext={nextStep} 
              onBack={prevStep}
              playClick={() => {}}
            />
          )}
          {currentStep === 6 && (
            <StudyEnvironmentStep 
              key="environment" 
              onNext={nextStep} 
              onBack={prevStep}
              userData={userData}
              setUserData={setUserData}
              playClick={() => {}}
            />
          )}
          {currentStep === 7 && (
            <FocusWellbeingStep 
              key="wellbeing" 
              onNext={nextStep} 
              onBack={prevStep}
              playClick={() => {}}
            />
          )}
          {currentStep === 8 && (
            <FinalChallengeStep 
              key="challenge" 
              onNext={nextStep} 
              onBack={prevStep}
              playClick={() => {}}
            />
          )}
          {currentStep === 9 && (
            <CompleteStep 
              key="complete" 
              userData={userData}
              onFinish={() => navigate('/subjects')}
              playComplete={() => {}}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Step 1: Welcome
function WelcomeStep({ onNext, playClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="w-24 h-24 bg-primary rounded-2xl border-2 border-foreground flex items-center justify-center mx-auto mb-6 shadow-[4px_4px_0px_0px_rgb(var(--9ready-shadow))]"
      >
        <Rocket size={48} weight="fill" className="text-white" />
      </motion.div>

      <h1 className="text-3xl sm:text-4xl font-heading font-black mb-4">
        Welcome to <span className="gradient-text">Grade9Ready</span>
      </h1>
      
      <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
        Let's get you ready for Grade 9. This quick journey will teach you 
        <span className="font-bold text-foreground"> how to revise smarter</span>, 
        stay motivated, and crush your goals.
      </p>

      <div className="space-y-4">
        <button
          onClick={() => { playClick(); onNext(); }}
          className="neo-btn text-lg px-8 py-4 flex items-center gap-3 mx-auto"
          data-testid="start-journey-btn"
        >
          Start Your Journey
          <ArrowRight size={24} weight="bold" />
        </button>

        <p className="text-sm text-muted-foreground">
          Takes about 5 minutes • Earn 100+ XP
        </p>
      </div>

      <div className="mt-12 flex justify-center gap-4">
        {[Trophy, Target, Brain, Fire].map((Icon, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + i * 0.1 }}
            className="w-12 h-12 bg-secondary rounded-lg border-2 border-foreground flex items-center justify-center"
          >
            <Icon size={24} weight="fill" className="text-primary" />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// Step 2: Why Revision
function WhyRevisionStep({ onNext, onBack, userData, setUserData, playClick }) {
  const motivations = [
    { id: 'college', label: 'Get into college/sixth form', icon: Books },
    { id: 'family', label: 'Make my family proud', icon: Heart },
    { id: 'personal', label: 'Personal achievement', icon: Trophy },
    { id: 'unsure', label: 'Not sure yet', icon: Question },
  ];

  const handleSelect = (id) => {
    playClick();
    setUserData(prev => ({ ...prev, motivation: id }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Why do you want to do well?
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Your answer helps us personalise your experience
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {motivations.map((m) => (
          <button
            key={m.id}
            onClick={() => handleSelect(m.id)}
            className={`neo-card !p-4 flex items-center gap-4 text-left transition-all ${
              userData.motivation === m.id 
                ? 'border-primary bg-primary/5 -translate-y-1' 
                : 'hover:-translate-y-0.5'
            }`}
            data-testid={`motivation-${m.id}`}
          >
            <div className={`w-12 h-12 rounded-lg border-2 border-foreground flex items-center justify-center ${
              userData.motivation === m.id ? 'bg-primary text-white' : 'bg-secondary'
            }`}>
              <m.icon size={24} weight="bold" />
            </div>
            <span className="font-bold">{m.label}</span>
            {userData.motivation === m.id && (
              <CheckCircle size={24} weight="fill" className="text-primary ml-auto" />
            )}
          </button>
        ))}
      </div>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={!!userData.motivation}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 3: How Revision Works (Swipeable Cards)
function HowRevisionWorksStep({ onNext, onBack, playClick }) {
  const [cardIndex, setCardIndex] = useState(0);
  
  const cards = [
    {
      icon: Brain,
      title: "Active Practice Beats Passive Reading",
      content: "Reading your notes over and over doesn't work. Testing yourself, solving problems, and explaining concepts out are 3x more effective.",
      color: "bg-indigo-500"
    },
    {
      icon: Target,
      title: "Mistakes Are Your Superpower",
      content: "Every mistake shows you exactly what to focus on. Embrace them! The best learners make lots of mistakes and learn from each one.",
      color: "bg-rose-500"
    },
    {
      icon: Lightning,
      title: "Build Understanding, Not Memory",
      content: "Don't just memorise facts. Understand why things work. When you truly get it, you'll remember it forever and can apply it anywhere.",
      color: "bg-emerald-500"
    }
  ];

  const nextCard = () => {
    playClick();
    setCardIndex(prev => Math.min(prev + 1, cards.length - 1));
  };

  const prevCard = () => {
    playClick();
    setCardIndex(prev => Math.max(prev - 1, 0));
  };

  const isLastCard = cardIndex === cards.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        How Revision Actually Works
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Swipe through to learn the science
      </p>

      {/* Card */}
      <div className="relative mb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={cardIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            className="neo-card"
          >
            <div className={`w-16 h-16 ${cards[cardIndex].color} rounded-xl border-2 border-foreground flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]`}>
              {(() => {
                const Icon = cards[cardIndex].icon;
                return <Icon size={32} weight="bold" className="text-white" />;
              })()}
            </div>
            <h3 className="font-heading font-bold text-xl mb-3">{cards[cardIndex].title}</h3>
            <p className="text-muted-foreground">{cards[cardIndex].content}</p>
          </motion.div>
        </AnimatePresence>

        {/* Card Navigation */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={prevCard}
            disabled={cardIndex === 0}
            className="p-3 rounded-lg border-2 border-foreground bg-card hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="card-prev"
            aria-label="Previous card"
          >
            <CaretLeft size={20} weight="bold" />
          </button>
          
          <div className="flex gap-2">
            {cards.map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full border-2 border-foreground ${
                  i === cardIndex ? 'bg-primary' : 'bg-secondary'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextCard}
            disabled={isLastCard}
            className="p-3 rounded-lg border-2 border-foreground bg-card hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="card-next"
            aria-label="Next card"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </div>
      </div>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={isLastCard}
        nextLabel="Got it!"
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 4: Beat Procrastination
function BeatProcrastinationStep({ onNext, onBack, userData, setUserData, playClick }) {
  const [showTip, setShowTip] = useState(false);

  const distractions = [
    { id: 'phone', label: 'My phone', icon: '📱' },
    { id: 'gaming', label: 'Gaming', icon: '🎮' },
    { id: 'friends', label: 'Friends/Social', icon: '👥' },
    { id: 'unknown', label: "Don't know", icon: '🤷' },
  ];

  // Specific actionable tips for each distraction
  const tips = {
    phone: {
      tip: "Put your phone in another room. Out of sight, out of mind! If this isn't possible, put it in flight mode.",
      goal: "Complete 1 quick Maths quiz on Grade9Ready before checking notifications your phone",
      action: "Try the Numbers section now - it only takes 5 minutes!"
    },
    gaming: {
      tip: "Use gaming as a reward system. Set a clear rule: study first, game after.",
      goal: "Finish 3 practice questions, then take a 10-minute gaming break",
      action: "Start with the Calculating Tips lesson - earn XP like levelling up!"
    },
    friends: {
      tip: "Create a study group with your friends! Challenge your friends to beat your scores.",
      goal: "Complete a topic and share your score with a friend",
      action: "Try the Mixed Practice section - see who gets the highest score!"
    },
    unknown: {
      tip: "Sometimes we don't notice what distracts us. Start small and build awareness.",
      goal: "Do just ONE lesson right now - that's your only focus",
      action: "Jump into the BODMAS lesson - it's quick and builds confidence!"
    }
  };

  const handleSelect = (id) => {
    setUserData(prev => ({ ...prev, distraction: id }));
    setShowTip(true);
  };

  const currentTip = userData.distraction ? tips[userData.distraction] : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Beat Procrastination
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        What usually distracts you?
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {distractions.map((d) => (
          <button
            key={d.id}
            onClick={() => handleSelect(d.id)}
            className={`neo-card !p-4 text-center transition-all ${
              userData.distraction === d.id 
                ? 'border-primary bg-primary/5 -translate-y-1' 
                : 'hover:-translate-y-0.5'
            }`}
            data-testid={`distraction-${d.id}`}
          >
            <span className="text-3xl mb-2 block">{d.icon}</span>
            <span className="font-bold text-sm">{d.label}</span>
          </button>
        ))}
      </div>

      {/* Personalized Tip with Specific Goal */}
      <AnimatePresence>
        {showTip && currentTip && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 mb-6"
          >
            {/* The Tip */}
            <div className="neo-card bg-accent/20">
              <div className="flex items-start gap-3">
                <Lightning size={24} weight="fill" className="text-primary flex-shrink-0" />
                <div>
                  <p className="font-bold mb-1">Your Strategy:</p>
                  <p className="text-muted-foreground">{currentTip.tip}</p>
                </div>
              </div>
            </div>

            {/* Specific Short-term Goal */}
            <div className="neo-card border-primary">
              <div className="flex items-start gap-3">
                <Target size={24} weight="fill" className="text-emerald-500 flex-shrink-0" />
                <div>
                  <p className="font-bold mb-1 text-emerald-600">Your Quick Win Goal:</p>
                  <p className="font-medium">{currentTip.goal}</p>
                  <p className="text-sm text-primary mt-2">{currentTip.action}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={!!userData.distraction}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 5: Smart Targets
function SmartTargetsStep({ onNext, onBack, userData, setUserData, playClick }) {
  const [showMission, setShowMission] = useState(false);

  const subjects = ['Maths', 'English', 'Science', 'History', 'Geography', 'Spanish'];
  
  // Topic suggestions based on subject for app exploration
  const topicSuggestions = {
    'Maths': ['BODMAS & Calculating', 'Ordering Numbers', 'Addition & Subtraction', 'Multiplying & Dividing'],
    'English': ['Comprehension Skills', 'Grammar Basics', 'Writing Techniques', 'Vocabulary Building'],
    'Science': ['Cells & Organisation', 'Chemical Reactions', 'Energy & Forces', 'Ecosystems'],
    'History': ['Source Analysis', 'Historical Significance', 'Cause & Effect', 'Interpretation'],
    'Geography': ['Rivers & Coasts', 'Weather & Climate', 'Urbanisation', 'Global Development'],
    'Spanish': ['Core Vocabulary', 'Grammar Basics', 'Translation Skills', 'Conversation']
  };
  
  const handleChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      target: { ...prev.target, [field]: value }
    }));
    if (field === 'subject') {
      // Reset topic when subject changes
      setUserData(prev => ({
        ...prev,
        target: { ...prev.target, subject: value, topic: '' }
      }));
      setShowMission(false);
    }
  };

  const generateMission = () => {
    setShowMission(true);
  };

  const { subject, topic, time } = userData.target;
  const canGenerate = subject && topic && time;
  const currentSuggestions = subject ? topicSuggestions[subject] || [] : [];

  // Generate specific app-exploration mission based on inputs
  const getMissionText = () => {
    if (!subject || !topic) return '';
    
    const missions = {
      'Maths': `Explore the ${topic} section in Maths. Complete the Learn module, try 5 practice questions, and aim for 80%+ on the quiz. Earn at least 50 XP!`,
      'English': `Dive into ${topic} in English. Read through the examples, practice 3 exercises, and use AI feedback on one written response.`,
      'Science': `Discover ${topic} in Science. Work through the explanation, complete the interactive diagram activity, and test yourself with the quick quiz.`,
      'History': `Study ${topic} in History. Analyse one source example, answer 3 practice questions, and review the model answer.`,
      'Geography': `Explore ${topic} in Geography. Learn the key concepts, label one diagram, and complete the 5-question checkpoint.`,
      'Spanish': `Work on ${topic} in Spanish. Master 10 vocabulary items, practice sentence building, and take the mini quiz.`
    };
    
    return missions[subject] || `Master ${topic} in ${subject} for ${time} minutes. Complete at least one lesson and quiz!`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Create Your First Target
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Smart goals keep you focused
      </p>

      <div className="neo-card mb-6">
        <div className="space-y-4">
          <div>
            <label className="block font-bold text-sm mb-2">Subject</label>
            <select
              value={subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              className="neo-input w-full"
              data-testid="target-subject"
            >
              <option value="">Choose a subject...</option>
              {subjects.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-sm mb-2">Topic to Focus On</label>
            {currentSuggestions.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {currentSuggestions.map(suggestion => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => handleChange('topic', suggestion)}
                      className={`p-2 text-sm rounded-lg border-2 transition-all ${
                        topic === suggestion 
                          ? 'border-primary bg-primary/10 font-bold' 
                          : 'border-foreground/30 hover:border-primary/50'
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => handleChange('topic', e.target.value)}
                  className="neo-input w-full text-sm"
                  placeholder="Or type your own topic..."
                  data-testid="target-topic"
                />
              </div>
            ) : (
              <input
                type="text"
                value={topic}
                onChange={(e) => handleChange('topic', e.target.value)}
                className="neo-input w-full"
                placeholder="First select a subject above"
                disabled={!subject}
                data-testid="target-topic"
              />
            )}
          </div>

          <div>
            <label className="block font-bold text-sm mb-2">Study Time: {time} minutes</label>
            <input
              type="range"
              min="10"
              max="60"
              step="5"
              value={time}
              onChange={(e) => handleChange('time', parseInt(e.target.value))}
              className="w-full accent-primary"
              data-testid="target-time"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10 min (quick)</span>
              <span>60 min (deep dive)</span>
            </div>
          </div>

          <button
            onClick={generateMission}
            disabled={!canGenerate}
            className="neo-btn w-full disabled:opacity-50"
            data-testid="generate-mission"
          >
            <Target size={20} weight="bold" />
            <span className="ml-2">Generate My Mission</span>
          </button>
        </div>
      </div>

      {/* Mission Output - Specific app exploration goal */}
      <AnimatePresence>
        {showMission && canGenerate && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neo-card bg-primary text-white mb-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <Trophy size={32} weight="fill" />
              <p className="text-sm font-bold uppercase tracking-wide">Your Mission</p>
            </div>
            <p className="text-lg leading-relaxed">
              {getMissionText()}
            </p>
            <div className="mt-4 pt-3 border-t border-white/30 flex items-center gap-2 text-sm">
              <Clock size={16} />
              <span>Target time: {time} minutes</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={showMission}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 6: Planning
function PlanningStep({ onNext, onBack, playClick }) {
  const [selectedDays, setSelectedDays] = useState([0, 2, 5]); // Mon, Wed, Sat by default
  const [planGenerated, setPlanGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const toggleDay = (index) => {
    setSelectedDays(prev => {
      if (prev.includes(index)) {
        return prev.filter(d => d !== index);
      } else {
        return [...prev, index].sort((a, b) => a - b);
      }
    });
    setPlanGenerated(false); // Reset plan when days change
  };

  const generatePlan = () => {
    if (selectedDays.length === 0) return;
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setPlanGenerated(true);
    }, 1500);
  };

  // Generate sessions based on selected days
  const subjects = ['Maths', 'English', 'Science', 'History', 'Geography'];
  const times = ['4:00 PM', '5:00 PM', '10:00 AM', '3:30 PM', '6:00 PM'];
  const durations = ['30 min', '25 min', '45 min', '20 min', '35 min', '10 min'];
  
  const weekPlan = selectedDays.map((dayIndex, i) => ({
    day: dayNames[dayIndex],
    subject: subjects[i % subjects.length],
    time: times[i % times.length],
    duration: durations[i % durations.length]
  }));

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Your Weekly Plan
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Consistency beats intensity
      </p>

      {/* Day Selection */}
      <div className="neo-card mb-6">
        <p className="font-bold text-sm mb-3">Select your study days:</p>
        <div className="grid grid-cols-7 gap-1 mb-4">
          {dayLabels.map((d, i) => (
            <button
              key={i}
              onClick={() => toggleDay(i)}
              className={`text-center py-3 rounded-lg text-sm font-bold transition-all ${
                selectedDays.includes(i) 
                  ? 'bg-primary text-white shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
              data-testid={`day-${dayNames[i].toLowerCase()}`}
            >
              {d}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center mb-4">
          Tap to toggle days • {selectedDays.length} days selected
        </p>
        
        {!planGenerated ? (
          <button
            onClick={generatePlan}
            disabled={generating || selectedDays.length === 0}
            className="neo-btn w-full disabled:opacity-50"
            data-testid="generate-plan"
          >
            {generating ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
                <span className="ml-2">Generating...</span>
              </>
            ) : (
              <>
                <Lightning size={20} weight="bold" />
                <span className="ml-2">Generate My Plan</span>
              </>
            )}
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            <p className="font-bold text-sm text-emerald-600 mb-2">Your personalized schedule:</p>
            {weekPlan.map((session, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-secondary rounded-lg border-2 border-foreground/30">
                <div className="w-12 h-12 bg-primary text-white rounded-lg flex flex-col items-center justify-center font-bold">
                  <span className="text-xs">{session.day}</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold">{session.subject}</p>
                  <p className="text-sm text-muted-foreground">{session.time} • {session.duration}</p>
                </div>
                <CheckCircle size={24} className="text-emerald-500" />
              </div>
            ))}
            <button
              onClick={() => setPlanGenerated(false)}
              className="text-sm text-primary font-bold hover:underline w-full text-center mt-2"
            >
              Change days & regenerate
            </button>
          </motion.div>
        )}
      </div>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={planGenerated}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 7: Study Environment
function StudyEnvironmentStep({ onNext, onBack, userData, setUserData, playClick }) {
  const [showTips, setShowTips] = useState(false);

  const places = [
    { id: 'bedroom', label: 'Bedroom', icon: '🛏️' },
    { id: 'library', label: 'Library', icon: '📚' },
    { id: 'livingroom', label: 'Living Room', icon: '🛋️' },
    { id: 'friends', label: 'With Friends', icon: '👥' },
  ];

  const placeInfo = {
    bedroom: {
      pros: ['Comfortable', 'Private', 'All your stuff nearby'],
      cons: ['Bed is tempting', 'Too comfortable?', 'Poor posture'],
      tips: ['Ideally work at a desk', 'Keep desk clear', 'Good lighting']
    },
    library: {
      pros: ['Quiet', 'No distractions', 'Study atmosphere'],
      cons: ['Need to travel', 'Can feel formal'],
      tips: ['Find your regular spot', 'Bring snacks', 'Use headphones']
    },
    livingroom: {
      pros: ['Space to spread out', 'Family nearby'],
      cons: ['TV temptation', 'Interruptions'],
      tips: ['TV off rule', 'Stick to set study times', 'Noise-cancelling headphones']
    },
    friends: {
      pros: ['Quiz each other', 'More fun', 'Support'],
      cons: ['Can get distracted', 'Chatting'],
      tips: ['Set study/break times', 'Work on same subject', 'Challenge each other']
    }
  };

  const handleSelect = (id) => {
    playClick();
    setUserData(prev => ({ ...prev, studyPlace: id }));
    setShowTips(true);
  };

  const info = userData.studyPlace ? placeInfo[userData.studyPlace] : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Your Study Space
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Where do you usually revise?
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {places.map((p) => (
          <button
            key={p.id}
            onClick={() => handleSelect(p.id)}
            className={`neo-card !p-4 text-center transition-all ${
              userData.studyPlace === p.id 
                ? 'border-primary bg-primary/5 -translate-y-1' 
                : 'hover:-translate-y-0.5'
            }`}
            data-testid={`place-${p.id}`}
          >
            <span className="text-3xl mb-2 block">{p.icon}</span>
            <span className="font-bold text-sm">{p.label}</span>
          </button>
        ))}
      </div>

      {/* Pros/Cons/Tips */}
      <AnimatePresence>
        {showTips && info && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="neo-card mb-6"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="font-bold text-emerald-600 mb-2">✓ Pros</p>
                <ul className="text-sm space-y-1">
                  {info.pros.map((p, i) => <li key={i}>• {p}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-bold text-rose-600 mb-2">✗ Cons</p>
                <ul className="text-sm space-y-1">
                  {info.cons.map((c, i) => <li key={i}>• {c}</li>)}
                </ul>
              </div>
            </div>
            <div className="pt-4 border-t border-foreground/20">
              <p className="font-bold text-primary mb-2">💡 Tips to improve</p>
              <ul className="text-sm space-y-1">
                {info.tips.map((t, i) => <li key={i}>• {t}</li>)}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={!!userData.studyPlace}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 8: Focus & Wellbeing
function FocusWellbeingStep({ onNext, onBack, playClick }) {
  const [breathing, setBreathing] = useState(false);
  const [breathPhase, setBreathPhase] = useState('ready'); // 'ready', 'inhale', 'hold', 'exhale', 'complete'
  const [breathCount, setBreathCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [exerciseComplete, setExerciseComplete] = useState(false);

  // Breathing cycle: 4 seconds inhale, 1 second hold, 6 seconds exhale = 11 seconds per breath
  // 3 breaths total = 33 seconds
  useEffect(() => {
    let timer;
    
    if (breathing && breathCount < 3) {
      if (breathPhase === 'inhale') {
        // Inhale for 4 counts
        setCountdown(4);
        timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setBreathPhase('hold');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (breathPhase === 'hold') {
        // Hold for 1 count
        setCountdown(1);
        timer = setTimeout(() => {
          setBreathPhase('exhale');
        }, 1000);
      } else if (breathPhase === 'exhale') {
        // Exhale for 6 counts
        setCountdown(6);
        timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setBreathCount(c => c + 1);
              // Check if we've done 3 breaths
              if (breathCount + 1 >= 3) {
                setBreathPhase('complete');
                setBreathing(false);
                setExerciseComplete(true);
                // Breathing exercise complete
              } else {
                setBreathPhase('inhale');
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }
    
    return () => {
      clearInterval(timer);
      clearTimeout(timer);
    };
  }, [breathing, breathPhase, breathCount]);

  const startBreathing = () => {
    setBreathing(true);
    setBreathPhase('inhale');
    setBreathCount(0);
    setExerciseComplete(false);
  };

  const stopBreathing = () => {
    setBreathing(false);
    setBreathPhase('ready');
    setCountdown(0);
  };

  const getPhaseLabel = () => {
    switch (breathPhase) {
      case 'inhale': return `Breathe In... ${countdown}`;
      case 'hold': return 'Hold...';
      case 'exhale': return `Breathe Out... ${countdown}`;
      case 'complete': return 'Well Done!';
      default: return 'Ready';
    }
  };

  const getScale = () => {
    if (!breathing) return 1;
    if (breathPhase === 'inhale') return 1 + (0.3 * (4 - countdown) / 4);
    if (breathPhase === 'hold') return 1.3;
    if (breathPhase === 'exhale') return 1.3 - (0.3 * (6 - countdown) / 6);
    return 1;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-2">
        Focus & Wellbeing
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        A calm mind learns better
      </p>

      {/* Breathing Exercise */}
      <div className="neo-card text-center mb-6">
        <Wind size={32} className="mx-auto mb-4 text-primary" />
        <h3 className="font-bold text-lg mb-2">Brain Reset Breathing</h3>
        <p className="text-sm text-muted-foreground mb-4">
          When you take a deep breath, it puts a break on your whole stress system. 
          It's your brain's reset button.
        </p>
        
        <div className="bg-secondary/50 rounded-lg p-3 mb-6 text-sm">
          <p className="font-medium">The Pattern:</p>
          <p className="text-muted-foreground">Breathe IN for 4 → Hold for 1 → Breathe OUT for 6</p>
          <p className="text-muted-foreground">Repeat 3 times</p>
        </div>
        
        <div className="relative w-40 h-40 mx-auto mb-4">
          <motion.div
            animate={{ scale: getScale() }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className={`w-full h-full rounded-full border-4 flex items-center justify-center ${
              exerciseComplete 
                ? 'bg-emerald-500/20 border-emerald-500' 
                : 'bg-primary/20 border-primary'
            }`}
          >
            <div className="text-center">
              <span className={`text-lg font-bold ${exerciseComplete ? 'text-emerald-600' : 'text-primary'}`}>
                {getPhaseLabel()}
              </span>
            </div>
          </motion.div>
        </div>

        {breathing && (
          <p className="text-sm text-muted-foreground mb-4">
            Breath {breathCount + 1} of 3
          </p>
        )}

        {exerciseComplete && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <p className="text-emerald-600 font-bold">
              Great job! Your brain is now reset and ready to learn.
            </p>
          </motion.div>
        )}

        {!exerciseComplete && (
          <button
            onClick={breathing ? stopBreathing : startBreathing}
            className={`neo-btn ${breathing ? '!bg-destructive' : ''}`}
            data-testid="breathing-toggle"
          >
            {breathing ? 'Stop' : 'Start Reset'}
          </button>
        )}
      </div>

      {/* Quick Tips */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="neo-card !p-4 text-center">
          <span className="text-2xl mb-2 block">💧</span>
          <p className="text-sm font-bold">Stay Hydrated</p>
        </div>
        <div className="neo-card !p-4 text-center">
          <span className="text-2xl mb-2 block">🚶</span>
          <p className="text-sm font-bold">Take Breaks</p>
        </div>
        <div className="neo-card !p-4 text-center">
          <span className="text-2xl mb-2 block">😴</span>
          <p className="text-sm font-bold">Get Quality Sleep</p>
        </div>
        <div className="neo-card !p-4 text-center">
          <span className="text-2xl mb-2 block">🍎</span>
          <p className="text-sm font-bold">Eat Smart</p>
        </div>
      </div>

      <NavigationButtons 
        onBack={onBack} 
        onNext={onNext} 
        canProceed={true}
        playClick={playClick}
      />
    </motion.div>
  );
}

// Step 9: Final Challenge
function FinalChallengeStep({ onNext, onBack, playClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="w-24 h-24 bg-accent rounded-2xl border-2 border-foreground flex items-center justify-center mx-auto mb-6 shadow-[4px_4px_0px_0px_rgb(var(--9ready-shadow))]"
      >
        <Fire size={48} weight="fill" className="text-orange-600" />
      </motion.div>

      <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
        Final Challenge
      </h2>
      
      <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
        You've learned the skills. Now it's time to put them into action.
        Complete your first real Grade9Ready session!
      </p>

      <div className="neo-card bg-primary/5 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary rounded-lg border-2 border-foreground flex items-center justify-center text-white">
            <Target size={24} weight="bold" />
          </div>
          <div className="text-left">
            <p className="font-bold">Your Challenge</p>
            <p className="text-sm text-muted-foreground">Complete one lesson in any subject</p>
          </div>
        </div>
      </div>

      <button
        onClick={() => { playClick(); onNext(); }}
        className="neo-btn text-lg px-8 py-4"
        data-testid="start-first-session"
      >
        <Rocket size={24} weight="bold" />
        <span className="ml-2">Start First Session</span>
      </button>

      <button
        onClick={onBack}
        className="block mx-auto mt-4 text-muted-foreground hover:text-foreground"
      >
        ← Go back
      </button>
    </motion.div>
  );
}

// Step 10: Complete
function CompleteStep({ userData, onFinish, playComplete }) {
  useEffect(() => {
    playComplete();
  }, [playComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      {/* Confetti animation simulation */}
      <div className="relative">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: 0, x: 0, opacity: 1 }}
            animate={{ 
              y: [0, -100, 200],
              x: [(i - 6) * 20, (i - 6) * 40],
              opacity: [1, 1, 0],
              rotate: [0, 360]
            }}
            transition={{ duration: 2, delay: i * 0.1 }}
            className="absolute left-1/2 top-0"
          >
            <Star size={20} weight="fill" className={`text-${['primary', 'accent', 'emerald-500', 'rose-500'][i % 4]}`} />
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="w-32 h-32 bg-emerald-500 rounded-full border-4 border-foreground flex items-center justify-center mx-auto mb-6 shadow-[6px_6px_0px_0px_rgb(var(--9ready-shadow))]"
      >
        <Trophy size={64} weight="fill" className="text-white" />
      </motion.div>

      <h1 className="text-3xl sm:text-4xl font-heading font-black mb-4">
        You're Ready to Begin!
      </h1>

      <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
        You've completed the Grade9Ready onboarding journey. Time to start your path to Grade 9!
      </p>

      {/* Rewards */}
      <div className="flex justify-center gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="neo-card !p-4 text-center"
        >
          <Star size={32} weight="fill" className="text-accent mx-auto mb-2" />
          <p className="text-2xl font-black text-primary">+{userData.xp + 100} XP</p>
          <p className="text-xs text-muted-foreground">Earned</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="neo-card !p-4 text-center"
        >
          <Medal size={32} weight="fill" className="text-amber-500 mx-auto mb-2" />
          <p className="font-bold">Badge Unlocked</p>
          <p className="text-xs text-muted-foreground">Revision Starter</p>
        </motion.div>
      </div>

      <button
        onClick={onFinish}
        className="neo-btn text-lg px-8 py-4"
        data-testid="finish-onboarding"
      >
        <Rocket size={24} weight="bold" />
        <span className="ml-2">Start Learning</span>
      </button>
    </motion.div>
  );
}

// Navigation Buttons Component
function NavigationButtons({ onBack, onNext, canProceed, nextLabel = "Continue", playClick }) {
  return (
    <div className="flex items-center justify-between">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
      >
        <CaretLeft size={20} />
        Back
      </button>
      <button
        onClick={() => { playClick(); onNext(); }}
        disabled={!canProceed}
        className="neo-btn disabled:opacity-50"
        data-testid="next-step"
      >
        {nextLabel}
        <CaretRight size={20} className="ml-2" />
      </button>
    </div>
  );
}
